import { useEffect, useState } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, RefreshCw, AlertCircle, Check, Sparkles, HardDrive, LogOut } from 'lucide-react';
import GoldRule from '../components/GoldRule';
import ImportPanel from '../components/ImportPanel';
import { EditCataloguePanel } from '../components/EditCataloguePanel';
import EditSetsPanel from '../components/EditSetsPanel';
import PaymentsPanel from '../components/PaymentsPanel';
import FulfilmentPanel from '../components/FulfilmentPanel';
import EnquiriesPanel from '../components/EnquiriesPanel';
import MediaLibraryPanel from '../components/MediaLibraryPanel';
import ImageStudioPanel from '../components/ImageStudioPanel';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { logout } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  // --- Sets state ---
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setPrompts, setSetPrompts] = useState({});
  const [setBusy, setSetBusy] = useState({});
  const [setResults, setSetResults] = useState({});
  const [bust, setBust] = useState(Date.now());

  // --- Storage status ---
  const [storage, setStorage] = useState(null);
  useEffect(() => {
    adminAxios.get(`${API}/storage/status`).then((r) => setStorage(r.data)).catch(() => setStorage({ available: false }));
  }, []);

  const loadSets = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminAxios.get(`${API}/admin/sets`);
      setSets(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSets(); }, []);

  const generateSet = async (slug) => {
    setSetBusy((b) => ({ ...b, [slug]: true }));
    setSetResults((r) => ({ ...r, [slug]: null }));
    try {
      const { data } = await adminAxios.post(`${API}/admin/generate-set-image`, {
        slug,
        prompt: setPrompts[slug] || null,
      });
      setSetResults((r) => ({
        ...r,
        [slug]: { ok: true, msg: `Saved → ${data.hero_image}`, used: data.used_codes, missing: data.missing_codes },
      }));
      setBust(Date.now());
      loadSets();
    } catch (e) {
      setSetResults((r) => ({
        ...r,
        [slug]: { ok: false, msg: e?.response?.data?.detail || e.message },
      }));
    } finally {
      setSetBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  return (
    <div data-testid="admin-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow text-mb-muted mb-8">Atelier · internal</div>
            <h1 className="font-display text-5xl md:text-6xl text-mb leading-[0.95]">
              Imagery <span className="italic">workshop.</span>
            </h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="mt-2 flex items-center gap-2 font-ui text-[12px] text-mb-muted hover:text-gold transition-colors duration-200 tracking-[0.15em] uppercase"
          >
            <LogOut size={14} />
            <span>Sign out</span>
          </button>
        </div>
        <div className="mt-10"><GoldRule size="med" /></div>
        <p className="mt-10 font-display italic text-[18px] text-mb-muted max-w-2xl leading-relaxed">
          Drop product photos into <code className="not-italic font-ui text-gold text-[12px]">/app/frontend/public/products/</code>{' '}
          using <code className="not-italic font-ui text-gold text-[12px]">gv-4439.jpg</code>. Clean any photo with Nano Banana
          or compose a set hero from the pieces you have.
        </p>
      </section>

      {error && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8">
          <div className="hairline border-gold p-5 bg-mb-surface flex gap-3 items-start">
            <AlertCircle size={16} className="text-gold mt-0.5" />
            <div>
              <div className="eyebrow text-gold mb-1">Backend error</div>
              <div className="text-[13px] text-mb-muted">{String(error)}</div>
            </div>
          </div>
        </section>
      )}

      {/* ---------- STORAGE STATUS ---------- */}
      {storage && (
        <section
          className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10"
          data-testid="admin-storage-status"
        >
          <div className={`hairline p-5 bg-mb-surface flex items-start gap-4 ${storage.available ? 'border-gold' : 'border-subtle'}`}>
            <HardDrive
              size={18}
              strokeWidth={1.25}
              className={storage.available ? 'text-gold mt-0.5' : 'text-mb-muted mt-0.5'}
            />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-4">
                <div className="eyebrow text-gold">Object storage</div>
                <div className={`eyebrow-sm ${storage.available ? 'text-gold' : 'text-mb-muted'}`}>
                  {storage.available ? '● Live' : '○ Local only'}
                </div>
              </div>
              <p className="mt-2 text-[13px] text-mb-muted leading-relaxed">
                {storage.available
                  ? `${storage.files_in_storage} of ${storage.files_registered} files backed up to Emergent object storage (namespace: ${storage.app_namespace}). Images are served through /api/files with disk fallback.`
                  : 'Falling back to local disk. Uploads will still work; add EMERGENT_LLM_KEY to enable cloud storage.'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ---------- ADD A PRODUCT ---------- */}
      <ImportPanel
        onChanged={() => {
          loadSets();
        }}
      />


      {/* ---------- CATALOGUE: Edit + Add pieces ---------- */}
      <EditCataloguePanel onChanged={loadSets} />

      {/* ---------- EDIT SETS ---------- */}
      <EditSetsPanel onChanged={loadSets} />

      {/* ---------- ENQUIRIES ---------- */}
      <EnquiriesPanel />

      {/* ---------- FULFILMENT QUEUE ---------- */}
      <FulfilmentPanel />

      {/* ---------- PAYMENTS ---------- */}
      <PaymentsPanel />

      {/* ---------- MEDIA LIBRARY ---------- */}
      <MediaLibraryPanel />

      {/* ---------- IMAGE STUDIO ---------- */}
      <ImageStudioPanel />

      {/* ---------- SET HEROES ---------- */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-sets-panel">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="eyebrow text-gold mb-4">Set heroes</div>
            <h2 className="font-display text-3xl md:text-4xl text-mb">Composed, five at a time.</h2>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-mb-muted">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {sets.map((s) => {
            const r = setResults[s.slug];
            return (
              <article
                key={s.slug}
                data-testid={`admin-set-${s.slug}`}
                className="bg-mb-surface hairline border-subtle p-6 md:p-8"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="tier-badge">{s.tier}</div>
                    <h3 className="mt-4 font-display text-2xl md:text-3xl text-mb">{s.name}</h3>
                  </div>
                  <div className="text-right eyebrow-sm text-mb-muted">
                    {s.available_items.length}/{s.items.length} photos
                  </div>
                </div>

                <div className="mt-4 aspect-[16/10] bg-mb hairline border-subtle flex items-center justify-center overflow-hidden">
                  {s.hero_image ? (
                    <img
                      key={`${s.slug}-${bust}`}
                      src={`${s.hero_image}?t=${bust}`}
                      alt={s.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center px-6">
                      <div className="eyebrow text-mb-muted">No hero yet</div>
                      <div className="mt-2 font-display italic text-mb-muted text-[14px]">Generate to compose one</div>
                    </div>
                  )}
                </div>

                <div className="mt-6 hairline-t border-subtle pt-4">
                  <div className="eyebrow-sm text-mb-muted mb-3">Inventory</div>
                  <div className="flex flex-wrap gap-2">
                    {s.items.map((code) => {
                      const has = s.available_items.includes(code);
                      return (
                        <span key={code} className={`feature-badge ${has ? '' : 'feature-badge--muted'}`} title={has ? 'photo present' : 'missing'}>
                          {has ? '●' : '○'} {code}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6">
                  <label className="mb-label">Custom prompt (optional)</label>
                  <textarea
                    data-testid={`admin-prompt-${s.slug}`}
                    rows={3}
                    className="mb-input"
                    placeholder="Leave blank to use the brand-aware default prompt."
                    value={setPrompts[s.slug] || ''}
                    onChange={(e) => setSetPrompts((p) => ({ ...p, [s.slug]: e.target.value }))}
                  />
                </div>

                <div className="mt-6 flex items-center gap-4 flex-wrap">
                  <button
                    className="btn-gold disabled:opacity-50"
                    disabled={setBusy[s.slug] || s.available_items.length === 0}
                    onClick={() => generateSet(s.slug)}
                    data-testid={`admin-generate-${s.slug}`}
                  >
                    {setBusy[s.slug] ? (
                      <><Loader2 size={14} className="animate-spin" /> Composing…</>
                    ) : (
                      <><RefreshCw size={14} strokeWidth={1.25} />{s.hero_image ? 'Regenerate hero' : 'Generate hero'}</>
                    )}
                  </button>
                  {s.available_items.length === 0 && (
                    <div className="eyebrow-sm text-mb-muted">Drop product photos first</div>
                  )}
                </div>

                {r && (
                  <div className={`mt-5 hairline p-4 ${r.ok ? 'border-gold' : 'border-subtle'}`} data-testid={`admin-result-${s.slug}`}>
                    <div className={`flex items-start gap-2 eyebrow-sm ${r.ok ? 'text-gold' : 'text-mb-muted'}`}>
                      {r.ok ? <Check size={14} /> : <AlertCircle size={14} />}
                      {r.ok ? 'Done' : 'Failed'}
                    </div>
                    <div className="mt-2 text-[13px] text-mb-muted break-words">{r.msg}</div>
                    {r.ok && r.missing?.length > 0 && (
                      <div className="mt-2 eyebrow-sm text-mb-muted">Missing photos: {r.missing.join(', ')}</div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
